import React, { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import './index.css'; // Tailwind CSS
import routes from './routes';
import { QUERY_KEYS } from './constants/queryKeys';
import { fetchCategoryList } from './apis/category';
import { bannerApi } from './apis/banner';
import { useGlobalAlertStore } from './store/globalAlert';
import { ThreeDotsSpinner } from '@/components/ui/ThreeDotsSpinner';
import GlobalErrorBoundary from '@/components/common/GlobalErrorBoundary';
import MaintenanceOverlay from '@/components/common/MaintenanceOverlay';

// 콘솔에서 언제든 바로 __ENV__ 입력하여 환경변수 확인할 수 있도록 전역 노출
if (typeof window !== 'undefined') {
  (window as any).__ENV__ = import.meta.env;
  if (import.meta.env.VITE_APP_ENV !== 'production') {
    import('./store/maintenance').then(({ useMaintenanceStore }) => {
      (window as any).__MAINTENANCE__ = {
        open: () => useMaintenanceStore.getState().setMaintenance(true),
        close: () => useMaintenanceStore.getState().setMaintenance(false),
        check: () => useMaintenanceStore.getState().triggerHealthCheck(),
        status: () => useMaintenanceStore.getState(),
      };
    });
  }
}

// 오직 운영 환경(VITE_APP_ENV === 'production')에서만 콘솔 로그 비활성화
if (import.meta.env.VITE_APP_ENV === 'production') {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.warn = () => {};
}

// 개발/스테이징/로컬 환경에서는 환경변수 및 위젯 설정 확인용 로그 출력
if (import.meta.env.VITE_APP_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('[Acepharm][ENV]', {
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_APP_API: import.meta.env.VITE_APP_API,
    VITE_TOSS_PAYMENTS_CLIENT_KEY: import.meta.env.VITE_TOSS_PAYMENTS_CLIENT_KEY,
    VITE_TOSS_BRANDPAY_MID: import.meta.env.VITE_TOSS_BRANDPAY_MID,
    VITE_ENABLE_API_DEBUG: import.meta.env.VITE_ENABLE_API_DEBUG,
    VITE_APP_LOGIN_INFO: import.meta.env.VITE_APP_LOGIN_INFO,
    MODE: import.meta.env.MODE,
    PROD: import.meta.env.PROD,
  });
}

import { queryClient } from './utils/queryClient';

void queryClient
  .prefetchQuery({
    queryKey: QUERY_KEYS.CATEGORY.ALL,
    queryFn: fetchCategoryList,
    staleTime: 1000 * 60 * 10,
  })
  .then(() => {
    // 카테고리 로드 후 배너 로드 (병렬로 처리해도 되지만, 우선순위 고려)
    // 하지만 여기서는 독립적으로 실행
  })
  .catch(error => {
    if (import.meta.env.DEV) {
      console.warn('[Acepharm] 카테고리 사전 로드 실패:', error);
    }
  });

import { getBannerImageUrl } from './utils/image';

// 메인 배너 사전 로드 & LCP 이미지 우선 프리로드
void queryClient
  .prefetchQuery({
    queryKey: QUERY_KEYS.BANNER.MAIN,
    queryFn: async () => {
      const response = await bannerApi.getMainBanners();
      if (response.isSuccess && response.result) {
        // LCP 최적화: 첫 번째 노출 배너 이미지를 브라우저 네트워크 최우선 순위로 즉시 Preload
        const firstBanner = response.result.find(b => b.exposed);
        if (firstBanner?.uploadFileInfo?.fileUrl) {
          const bannerUrl = getBannerImageUrl(firstBanner.uploadFileInfo.fileUrl);
          if (bannerUrl && typeof document !== 'undefined') {
            const preloadLink = document.createElement('link');
            preloadLink.rel = 'preload';
            preloadLink.as = 'image';
            preloadLink.href = bannerUrl;
            (preloadLink as any).fetchPriority = 'high';
            document.head.appendChild(preloadLink);
          }
        }
        return response.result;
      }
      throw new Error(response.message || 'Main banners fetch failed');
    },
    staleTime: 1000 * 60, // 1분
  })
  .catch(error => {
    if (import.meta.env.DEV) {
      console.warn('[Acepharm] 메인 배너 사전 로드 실패:', error);
    }
  });

const loading = (
  <div className='flex items-center justify-center h-screen'>
    <ThreeDotsSpinner />
  </div>
);

// Layout
const DefaultLayout = React.lazy(() => import('./components/layout/DefaultLayout'));

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

const renderApp = () => {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#0077BD',
            },
          }}
        >
          <GlobalErrorBoundary>
            <MaintenanceOverlay />
            <BrowserRouter>
              <Suspense fallback={loading}>
                <Routes>
                  {routes.map(route => {
                    const Component = route.element;
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <DefaultLayout>
                            <Component />
                          </DefaultLayout>
                        }
                      />
                    );
                  })}
                </Routes>
              </Suspense>
            </BrowserRouter>
          </GlobalErrorBoundary>
        </ConfigProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
};

renderApp();
